import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useAnalyticsData } from "@/hooks/useAnalyticsData";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { CandidateHeader } from "@/components/candidate/Header";
import { CandidateSidebar } from "@/components/candidate/Sidebar";
import { MetricsCards } from "@/components/admin/analytics/MetricsCards";
import { UsersGrowthChart } from "@/components/admin/analytics/UsersGrowthChart";
import { CompaniesGrowthChart } from "@/components/admin/analytics/CompaniesGrowthChart";
import { JobViewsChart } from "@/components/admin/analytics/JobViewsChart";
import { EngagementChart } from "@/components/admin/analytics/EngagementChart";
import { UserDistributionChart } from "@/components/admin/analytics/UserDistributionChart";
import { ExternalLinksStats } from "@/components/admin/analytics/ExternalLinksStats";
import { RegisteredCompaniesTable } from "@/components/admin/analytics/RegisteredCompaniesTable";
import { RevenueStats } from "@/components/admin/analytics/RevenueStats";
import { DateRangePicker } from "@/components/admin/analytics/DateRangePicker";
import { UserActivityPanel } from "@/components/admin/analytics/UserActivityPanel";
import { Button } from "@/components/ui/button";
import { RefreshCw, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format, subDays, differenceInDays, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Analytics() {
  usePageMeta({
    title: "Analytics - Data Hire Club",
    description: "Analytics administrativo",
    noindex: true,
  });

  const navigate = useNavigate();
  const { isAdmin, isLoading: isLoadingAdmin } = useIsAdmin();
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const { data: analytics, isLoading, refetch, isFetching, dataUpdatedAt } = useAnalyticsData(startDate, endDate);

  useEffect(() => {
    if (!isLoadingAdmin && !isAdmin) {
      navigate("/candidate/dashboard");
    }
  }, [isAdmin, isLoadingAdmin, navigate]);

  if (isLoadingAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <CandidateHeader />
        <div className="flex">
          <div className="hidden md:block">
            <CandidateSidebar />
          </div>
          <main className="flex-1 py-8 px-4 md:px-8 md:ml-64">
            <Skeleton className="h-[400px]" />
          </main>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const handleRefresh = () => {
    refetch();
  };

  return (
    <CandidateLayout maxWidth="7xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Exibindo dados de {format(parseISO(startDate), 'dd/MM/yyyy')} até {format(parseISO(endDate), 'dd/MM/yyyy')}
            {' '}({differenceInDays(parseISO(endDate), parseISO(startDate))} dias)
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
            <Clock className="h-3 w-3" />
            <span>Última atualização: {dataUpdatedAt ? format(new Date(dataUpdatedAt), "HH:mm", { locale: ptBR }) : '--:--'}</span>
            <span className="text-muted-foreground/60">• Atualiza automaticamente a cada hora</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
          
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="icon"
            disabled={isFetching}
            className={isFetching ? "animate-spin" : ""}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))}
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))}
          </div>
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
            <Skeleton className="h-[400px]" />
            <Skeleton className="h-[400px]" />
          </div>
        </div>
      ) : analytics ? (
        <div className="space-y-6">
          <MetricsCards
            totalUsers={analytics.totalUsers}
            totalCompanies={analytics.totalCompanies}
            totalActiveJobs={analytics.totalActiveJobs}
            totalJobViews={analytics.totalJobViews}
            newUsersInPeriod={analytics.newUsersInPeriod}
            newCompaniesInPeriod={analytics.newCompaniesInPeriod}
            postsInPeriod={analytics.postsInPeriod}
            jobViewsInPeriod={analytics.jobViewsInPeriod}
            likesInPeriod={analytics.likesInPeriod}
            commentsInPeriod={analytics.commentsInPeriod}
            commentLikesInPeriod={analytics.commentLikesInPeriod}
            datasetClicksInPeriod={analytics.datasetClicksInPeriod}
            challengeAttemptsInPeriod={analytics.challengeAttemptsInPeriod}
            correctChallengesInPeriod={analytics.correctChallengesInPeriod}
            certificatesIssuedInPeriod={analytics.certificatesIssuedInPeriod}
            pwaInstallsTotal={analytics.pwaInstallsTotal}
            pwaInstallsInPeriod={analytics.pwaInstallsInPeriod}
            candidateSubsActive={analytics.candidateSubsActive}
            candidateTrialsActive={analytics.candidateTrialsActive}
            candidatePremiumMonthly={analytics.candidatePremiumMonthly}
            candidatePremiumYearly={analytics.candidatePremiumYearly}
            candidateMrr={analytics.candidateMrr}
            candidateNewPremiumInPeriod={analytics.candidateNewPremiumInPeriod}
            candidateNewTrialsInPeriod={analytics.candidateNewTrialsInPeriod}
            candidateConversionsInPeriod={analytics.candidateConversionsInPeriod}
          />

          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
            <UsersGrowthChart data={analytics.usersGrowth} />
            <CompaniesGrowthChart data={analytics.companiesGrowth} />
          </div>

          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
            <JobViewsChart data={analytics.topJobs} />
            <UserDistributionChart data={analytics.userDistribution} />
          </div>

          <EngagementChart data={analytics.engagement} />

          <ExternalLinksStats data={analytics.externalLinks} />

          <RevenueStats
            mrr={analytics.mrr}
            activeSubscriptionsCount={analytics.activeSubscriptionsCount}
            subscriptionsByPlan={analytics.subscriptionsByPlan}
            revenueInPeriod={analytics.revenueInPeriod}
            paymentsInPeriod={analytics.paymentsInPeriod}
            subscriptionsList={analytics.subscriptionsList}
          />

          <RegisteredCompaniesTable data={analytics.companiesList} />

          <UserActivityPanel startDate={startDate} endDate={endDate} />
        </div>
      ) : null}
    </CandidateLayout>
  );
}
